// @ts-strict-ignore
import { component } from "picoapp";

export const scrollTriggerVideo = component(async (node: HTMLElement) => {
  let unmountCallback: () => void = null;
  if (node.dataset.src) {
    unmountCallback = await useCanvasFrames(node);
  } else {
    unmountCallback = useVideoFrames(node);
  }

  return unmountCallback;
});

const useVideoFrames = (node: HTMLElement) => {
  const video = node.querySelector<HTMLVideoElement>("video");
  if (!video) {
    throw Error("scrollTriggerVideo is missing <video> element");
  }

  let animationFrame = null;
  let observer = null;

  const animateVideo = () => {
    const scrollPercentage = getCurrentScrollPercentage(node);
    const updateTimeTo = video.duration * scrollPercentage;
    if (video.currentTime !== updateTimeTo) {
      video.currentTime = updateTimeTo;
    }
    animationFrame = window.requestAnimationFrame(animateVideo);
  };

  const enableScrollPlay = () => {
    observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && Math.round(target.intersectionRatio)) {
          window.requestAnimationFrame(animateVideo);
        } else {
          if (animationFrame) {
            window.cancelAnimationFrame(animationFrame);
          }
        }
      },
      { threshold: [0, 1] }
    );

    observer.observe(node);
  };

  video.addEventListener("canplaythrough", enableScrollPlay);

  return () => {
    video.removeEventListener("canplaythrough", enableScrollPlay);
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    if (observer) observer.disconnect();
  };
};

const useCanvasFrames = async (node: HTMLElement) => {
  const videoSrc = node.dataset.src;
  if (!videoSrc) {
    throw Error("scrollTriggerVideo is missing video src");
  }

  const posterImage = node.querySelector('[data-tbsp-component="image"]');
  const frameCount = node.dataset.frames || null;
  const frames = await unpackVideoToCanvas(videoSrc, parseInt(frameCount));

  const canvas = document.createElement("canvas");
  canvas.classList.add("w-full");
  canvas.height = frames[0].height;
  canvas.width = frames[0].width;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(frames[0], 0, 0);
  posterImage.classList.add("hidden");
  node.appendChild(canvas);

  let animationFrame = null;
  let currentFrame = null;

  const animateCanvas = () => {
    const scrolledPercentage = getCurrentScrollPercentage(node);
    const frameIndex = Math.floor(scrolledPercentage * (frames.length - 1));
    const hasFrame = frameIndex > 0 && frameIndex < frames.length - 1;
    if (frameIndex !== currentFrame && hasFrame) {
      ctx.drawImage(frames[frameIndex], 0, 0);
      currentFrame = frameIndex;
    }
    animationFrame = window.requestAnimationFrame(animateCanvas);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && Math.round(target.intersectionRatio)) {
        window.requestAnimationFrame(animateCanvas);
      } else {
        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
        }
      }
    },
    { threshold: [0, 1] }
  );

  observer.observe(node);

  return () => {
    observer.disconnect();
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
  };
};

const waitForCanPlayThrough = async (videoElement) => {
  return new Promise<void>((resolve) => {
    const handler = () => {
      videoElement.removeEventListener("canplaythrough", handler);
      resolve();
    };

    videoElement.addEventListener("canplaythrough", handler);
  });
};

const waitForSeeked = async (videoElement) => {
  return new Promise<void>((resolve) => {
    const handler = () => {
      videoElement.removeEventListener("seeked", handler);
      resolve();
    };
    videoElement.addEventListener("seeked", handler);
  });
};

const unpackVideoToCanvas = async (videoSrc: string, frameCount: number) => {
  const video = document.createElement("video");
  video.crossOrigin = "Anonymous";
  video.src = videoSrc;
  video.muted = true;
  video.load();
  await waitForCanPlayThrough(video);
  const { duration, videoHeight, videoWidth } = video;
  video.currentTime = 0;
  await waitForSeeked(video);

  let canvas;
  if (window.OffscreenCanvas) {
    canvas = new OffscreenCanvas(videoWidth, videoHeight);
  } else {
    canvas = document.createElement("canvas");
    canvas.width = videoWidth;
    canvas.height = videoHeight;
  }
  const context = canvas.getContext("2d", { willReadFrequently: true });

  const frames = [];
  const timeStep = frameCount ? duration / frameCount : 1;
  for (let i = 0; i <= duration; i += timeStep) {
    video.currentTime = i;
    await waitForSeeked(video);
    context.drawImage(video, 0, 0, videoWidth, videoHeight);
    const imageData = context.getImageData(0, 0, videoWidth, videoHeight);
    const imageBitmap = await createImageBitmap(imageData);
    frames.push(imageBitmap);
  }

  return frames;
};

const getCurrentScrollPercentage = (node: HTMLElement): number => {
  const { height } = node.getBoundingClientRect();
  const { clientHeight, scrollTop } = document.documentElement;
  const videoYStartPosition = node.offsetTop - clientHeight + height;
  const videoYEndPosition = node.offsetTop;
  const scrollDiff = videoYEndPosition - videoYStartPosition;
  const scrollPercentage = (scrollTop - videoYStartPosition) / scrollDiff;
  return Math.round(scrollPercentage * 100) / 100;
};
